import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { Reveal } from "@/components/demo/lib/Reveal";
import { IMG } from "@/components/demo/lib/images";

const WA = "41765008176";

const LANGS = ["IT", "EN", "DE", "FR"] as const;
type Lang = typeof LANGS[number];

const COPY: Record<Lang, { tag: string; h1a: string; h1b: string; sub: string; cta: string; nav: { p: string; m: string; mort: string; tour: string } }> = {
  IT: { tag: "Lago di Lugano · Dal 2008", h1a: "Proprietà esclusive,", h1b: "ville sul lago.", sub: "Una selezione riservata di residenze fronte lago e dimore d'autore per una clientela internazionale.", cta: "Richiedi una visita privata", nav: { p: "Proprietà", m: "Mappa", mort: "Mutuo", tour: "Virtual tour" } },
  EN: { tag: "Lake Lugano · Est. 2008", h1a: "Exclusive estates,", h1b: "lakefront villas.", sub: "A confidential selection of waterfront residences and signature homes for an international clientele.", cta: "Request a private viewing", nav: { p: "Properties", m: "Map", mort: "Mortgage", tour: "Virtual tour" } },
  DE: { tag: "Luganersee · Seit 2008", h1a: "Exklusive Anwesen,", h1b: "Seevillen.", sub: "Eine vertrauliche Auswahl von Seegrundstücken und Designer-Residenzen für eine internationale Kundschaft.", cta: "Private Besichtigung anfragen", nav: { p: "Objekte", m: "Karte", mort: "Hypothek", tour: "Virtual tour" } },
  FR: { tag: "Lac de Lugano · Depuis 2008", h1a: "Propriétés exclusives,", h1b: "villas au bord du lac.", sub: "Une sélection confidentielle de résidences au bord du lac et de demeures d'auteur pour une clientèle internationale.", cta: "Demander une visite privée", nav: { p: "Propriétés", m: "Carte", mort: "Hypothèque", tour: "Visite virtuelle" } },
};

const PROPS = [
  { id: "1", name: "Villa Aurora", loc: "Castagnola", price: 12500000, br: 6, sqm: 720, type: "Villa", img: IMG.estate.villa1 },
  { id: "2", name: "Attico Belvedere", loc: "Lugano centro", price: 5900000, br: 4, sqm: 320, type: "Attico", img: IMG.estate.villa2 },
  { id: "3", name: "Tenuta del Brè", loc: "Brè-Aldesago", price: 18900000, br: 12, sqm: 1200, type: "Tenuta", img: IMG.estate.villa3 },
  { id: "4", name: "Casa Lago", loc: "Morcote", price: 7200000, br: 5, sqm: 480, type: "Villa", img: IMG.estate.villa4 },
  { id: "5", name: "Chalet Verbier", loc: "Verbier", price: 9800000, br: 6, sqm: 520, type: "Chalet", img: IMG.estate.villa5 },
  { id: "6", name: "Loft Industriale", loc: "Mendrisio", price: 3400000, br: 3, sqm: 380, type: "Loft", img: IMG.estate.villa6 },
];

const MAP_PINS = [
  { x: 32, y: 58, id: "1" },
  { x: 48, y: 52, id: "2" },
  { x: 58, y: 64, id: "3" },
  { x: 25, y: 78, id: "4" },
  { x: 78, y: 22, id: "5" },
  { x: 22, y: 40, id: "6" },
];

function chf(n: number) { return "CHF " + n.toLocaleString("it-CH"); }

function Alpine() {
  const [lang, setLang] = useState<Lang>("IT");
  const t = COPY[lang];
  const [maxPrice, setMaxPrice] = useState(25000000);
  const [type, setType] = useState("Tutte");
  const [downpct, setDownpct] = useState(35);
  const [rate, setRate] = useState(1.8);
  const [years, setYears] = useState(20);
  const [picked, setPicked] = useState(PROPS[0]);

  const filtered = useMemo(() => PROPS.filter((p) => p.price <= maxPrice && (type === "Tutte" || p.type === type)), [maxPrice, type]);
  const loan = picked.price * (1 - downpct / 100);
  const monthlyRate = rate / 100 / 12;
  const n = years * 12;
  const monthly = monthlyRate === 0 ? loan / n : (loan * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);

  return (
    <div className="min-h-screen bg-[#0c0e10] text-stone-100 antialiased overflow-x-hidden">
      <style>{`.ff-serif { font-family: 'Cormorant Garamond', 'Times New Roman', serif } body { font-family: 'Inter', sans-serif }`}</style>

      <header className="fixed top-0 inset-x-0 z-50 bg-[#0c0e10]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-10 py-4 sm:py-5 flex items-center justify-between gap-3">
          <div className="ff-serif text-xl sm:text-2xl tracking-wide whitespace-nowrap">Alpine <span className="text-emerald-400">·</span> Estates</div>
          <nav className="hidden lg:flex gap-8 text-[11px] tracking-[0.3em] uppercase text-white/70">
            <a href="#properties" className="hover:text-emerald-400">{t.nav.p}</a>
            <a href="#map" className="hover:text-emerald-400">{t.nav.m}</a>
            <a href="#mortgage" className="hover:text-emerald-400">{t.nav.mort}</a>
            <a href="#tour" className="hover:text-emerald-400">{t.nav.tour}</a>
          </nav>
          <div className="flex items-center gap-2 sm:gap-4 shrink-0">
            <div className="hidden sm:flex gap-1 text-[10px] tracking-widest">
              {LANGS.map((l) => (
                <button key={l} onClick={() => setLang(l)} className={`px-2 py-1 ${lang === l ? "text-emerald-400" : "text-white/40 hover:text-white"}`}>{l}</button>
              ))}
            </div>
            <a href="#contact" className="text-[10px] sm:text-[11px] tracking-[0.2em] sm:tracking-[0.3em] uppercase border border-emerald-400/50 text-emerald-400 px-3 sm:px-4 py-2 hover:bg-emerald-400 hover:text-[#0c0e10] transition whitespace-nowrap">
              <span className="sm:hidden">Contatti</span><span className="hidden sm:inline">{t.cta}</span>
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative min-h-screen flex items-end pb-20 px-6 sm:px-10 pt-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMG.estate.hero} alt="Villa fronte lago di Lugano al tramonto" className="w-full h-full object-cover scale-110 origin-center" style={{ animation: "kenburns 24s ease-in-out infinite alternate" }} />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0c0e10] via-[#0c0e10]/30 to-[#0c0e10]/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto w-full">
          <div className="text-[10px] tracking-[0.4em] uppercase text-emerald-400 mb-8">— {t.tag}</div>
          <h1 className="ff-serif text-6xl sm:text-8xl lg:text-[8.5vw] leading-[0.92] max-w-5xl break-words">
            {t.h1a}<br /><em className="text-emerald-300">{t.h1b}</em>
          </h1>
          <p className="mt-10 text-white/70 text-lg max-w-xl leading-relaxed">{t.sub}</p>
        </div>
      </section>

      {/* PROPERTIES */}
      <section id="properties" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
              <h2 className="ff-serif text-5xl lg:text-7xl break-words">Proprietà <em>in vetrina.</em></h2>
              <div className="flex gap-2 flex-wrap">
                {["Tutte", "Villa", "Attico", "Tenuta", "Chalet", "Loft"].map((tp) => (
                  <button key={tp} onClick={() => setType(tp)} className={`px-4 py-2 text-[10px] tracking-[0.25em] uppercase border transition ${type === tp ? "bg-emerald-400 text-[#0c0e10] border-emerald-400" : "border-white/20 hover:border-emerald-400"}`}>{tp}</button>
                ))}
              </div>
            </div>
          </Reveal>
          <div className="grid lg:grid-cols-12 gap-4">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 40} className={i === 0 ? "lg:col-span-8" : "lg:col-span-4"}>
                <button onClick={() => setPicked(p)} className={`group block w-full text-left aspect-[4/3] relative overflow-hidden border ${picked.id === p.id ? "border-emerald-400" : "border-white/10 hover:border-emerald-400/50"} transition`}>
                  <img src={p.img} alt={`${p.name} a ${p.loc}`} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />
                  <div className="absolute bottom-0 inset-x-0 p-6">
                    <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400">{p.loc}</div>
                    <div className="ff-serif text-3xl mt-1">{p.name}</div>
                    <div className="mt-3 flex justify-between items-end text-sm text-white/70">
                      <span>{p.sqm} m² · {p.br} camere</span>
                      <span className="ff-serif text-2xl text-white">{chf(p.price)}</span>
                    </div>
                  </div>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MAP */}
      <section id="map" className="px-6 sm:px-10 py-24 border-t border-white/10 bg-[#0a0c0e]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[1fr_2fr] gap-10">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-emerald-400 mb-4">Mappa interattiva</div>
            <h2 className="ff-serif text-4xl lg:text-5xl mb-8 break-words">Filtra il portafoglio.</h2>
            <div className="space-y-6">
              <div>
                <div className="text-xs tracking-wider uppercase text-white/60 mb-2">Prezzo massimo: {chf(maxPrice)}</div>
                <input type="range" min={2000000} max={25000000} step={500000} value={maxPrice} onChange={(e) => setMaxPrice(+e.target.value)} className="w-full accent-emerald-400" />
              </div>
              <div className="text-sm text-white/70">{filtered.length} proprietà corrispondono ai tuoi criteri.</div>
            </div>
          </div>
          <div className="relative aspect-[4/3] bg-gradient-to-br from-emerald-950/40 via-[#0c0e10] to-stone-900 border border-white/10 overflow-hidden">
            <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 30% 60%, rgba(16,185,129,0.3), transparent 35%), radial-gradient(circle at 70% 40%, rgba(16,185,129,0.2), transparent 30%)" }} />
            <svg className="absolute inset-0 w-full h-full opacity-20" viewBox="0 0 100 100" preserveAspectRatio="none"><path d="M0,70 Q30,50 50,65 T100,55" stroke="rgb(52 211 153)" strokeWidth="0.3" fill="none" /></svg>
            {MAP_PINS.map((pin) => {
              const prop = PROPS.find((p) => p.id === pin.id)!;
              const visible = filtered.some((p) => p.id === pin.id);
              return (
                <button key={pin.id} onClick={() => setPicked(prop)} style={{ left: `${pin.x}%`, top: `${pin.y}%` }} className={`absolute -translate-x-1/2 -translate-y-1/2 transition ${visible ? "opacity-100" : "opacity-20"}`}>
                  <div className={`h-3 w-3 rounded-full ring-4 ring-emerald-400/20 ${picked.id === pin.id ? "bg-emerald-300 scale-150" : "bg-emerald-400"} transition-transform`} />
                  <div className="absolute left-4 top-0 ff-serif text-sm whitespace-nowrap bg-[#0c0e10]/90 px-2 py-1 border border-white/10">{prop.name}</div>
                </button>
              );
            })}
            <div className="absolute bottom-4 left-4 text-[10px] tracking-[0.3em] uppercase text-white/50">Lago di Lugano — Vista schematica</div>
          </div>
        </div>
      </section>

      {/* MORTGAGE CALC */}
      <section id="mortgage" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-[2fr_3fr] gap-12">
          <div>
            <div className="text-[10px] tracking-[0.4em] uppercase text-emerald-400 mb-4">Simulatore mutuo</div>
            <h2 className="ff-serif text-4xl lg:text-6xl leading-tight mb-4 break-words">Simula<br />la tua rata.</h2>
            <p className="text-white/60 max-w-md">Calcolo indicativo basato sulla proprietà selezionata. Per un'analisi accurata, ti mettiamo in contatto con il nostro broker partner.</p>
            <div className="mt-8 border border-white/10 p-5">
              <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400 mb-2">Proprietà selezionata</div>
              <div className="ff-serif text-2xl">{picked.name}</div>
              <div className="text-sm text-white/50 mt-1">{picked.loc} · {chf(picked.price)}</div>
            </div>
          </div>
          <div className="bg-[#0a0c0e] border border-white/10 p-8">
            <div className="grid sm:grid-cols-3 gap-6">
              <div>
                <div className="text-xs tracking-wider uppercase text-white/60 mb-2">Anticipo {downpct}%</div>
                <input type="range" min={20} max={60} value={downpct} onChange={(e) => setDownpct(+e.target.value)} className="w-full accent-emerald-400" />
                <div className="text-sm mt-1">{chf(picked.price * downpct / 100)}</div>
              </div>
              <div>
                <div className="text-xs tracking-wider uppercase text-white/60 mb-2">Tasso {rate.toFixed(2)}%</div>
                <input type="range" min={1} max={5} step={0.1} value={rate} onChange={(e) => setRate(+e.target.value)} className="w-full accent-emerald-400" />
              </div>
              <div>
                <div className="text-xs tracking-wider uppercase text-white/60 mb-2">Durata {years} anni</div>
                <input type="range" min={5} max={30} value={years} onChange={(e) => setYears(+e.target.value)} className="w-full accent-emerald-400" />
              </div>
            </div>
            <div className="mt-10 pt-8 border-t border-white/10 grid sm:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Capitale finanziato</div>
                <div className="ff-serif text-3xl mt-2">{chf(Math.round(loan))}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-emerald-400">Rata mensile</div>
                <div className="ff-serif text-5xl mt-2 text-emerald-300">{chf(Math.round(monthly))}</div>
              </div>
              <div>
                <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Interessi totali</div>
                <div className="ff-serif text-3xl mt-2">{chf(Math.round(monthly * n - loan))}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VIRTUAL TOUR */}
      <section id="tour" className="px-6 sm:px-10 py-24 border-t border-white/10">
        <div className="max-w-6xl mx-auto text-center">
          <Reveal>
            <div className="text-[10px] tracking-[0.4em] uppercase text-emerald-400 mb-4">— Esperienza virtuale</div>
            <h2 className="ff-serif text-5xl lg:text-7xl leading-tight break-words">Visita ogni stanza<br /><em>direttamente da casa.</em></h2>
            <p className="text-white/60 max-w-xl mx-auto mt-6">Ogni proprietà include tour virtuale Matterport 3D, video drone in 4K e planimetrie interattive. Visite private organizzate su appuntamento.</p>
          </Reveal>
          <Reveal delay={120}>
            <div className="mt-12 aspect-video bg-black border border-white/10 grid place-items-center relative overflow-hidden">
              <img src={IMG.estate.villa1} alt="Tour virtuale di una villa fronte lago" className="absolute inset-0 w-full h-full object-cover opacity-50" />
              <button className="relative h-20 w-20 rounded-full bg-emerald-400 text-[#0c0e10] grid place-items-center hover:scale-110 transition text-xl">▶</button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CONTACT */}
      <section id="contact" className="px-6 sm:px-10 py-32 border-t border-white/10 bg-emerald-950/30">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="ff-serif text-5xl lg:text-7xl break-words">Una visita <em className="text-emerald-300">privata.</em></h2>
          <p className="text-white/60 mt-6 max-w-lg mx-auto">Catalogo completo riservato. Discrezione garantita. Risposta entro 4 ore.</p>
          <a href={`https://wa.me/${WA}?text=${encodeURIComponent("Buongiorno Alpine Estates, sono interessato/a a vedere alcune proprietà.")}`} className="mt-10 inline-block bg-emerald-400 text-[#0c0e10] px-10 py-5 text-[11px] tracking-[0.3em] uppercase font-bold hover:bg-white transition">Richiedi una visita su WhatsApp</a>
        </div>
      </section>

      <footer className="px-6 sm:px-10 py-10 border-t border-white/10 text-xs text-white/40">
        <div className="max-w-7xl mx-auto flex flex-wrap justify-between gap-4 tracking-wider">
          <div>ALPINE ESTATES · Via Nassa 17, 6900 Lugano · Membro SVIT Ticino</div>
          <div>Demo FlashWeb · <Link to="/" className="underline">flash-web.ch</Link></div>
        </div>
      </footer>

      <style>{`@keyframes kenburns { 0% { transform: scale(1) translate(0,0) } 100% { transform: scale(1.15) translate(-2%, -1%) } }`}</style>
    </div>
  );
}

export const Route = createFileRoute("/demo/alpine-estates")({
  head: () => ({
    meta: [
      { title: "Alpine Estates — Ville sul Lago di Lugano (Demo FlashWeb)" },
      { name: "description", content: "Proprietà esclusive e ville fronte lago a Lugano e Verbier. Mappa interattiva, simulatore mutuo e visite private su appuntamento." },
      { name: "robots", content: "noindex, follow" },
    ],
  }),
  component: Alpine,
});
